import { createContext, useCallback, useContext, useRef } from "react";
import { driver, type Driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import type { TFunction } from "i18next";
import type { PageType } from "../../types";
import type { ProfileType } from "../../lib/client-guide-content";
import { getWalkthrough, getWalkthroughsForProfile, type GuideStep, type Walkthrough } from "./walkthroughs";

/** Clé localStorage lue par HelpPage pour défiler vers une section précise. */
export const HELP_SCROLL_KEY = "bizcoach_help_scroll_to";

/** Contexte permettant aux pages enfants (ex. HelpPage) de lancer une visite. */
export const GuideContext = createContext<{ runWalkthrough: (id: string) => void } | null>(null);

/** Hook de consommation du contexte de guide (null si hors provider). */
export function useGuide() {
  return useContext(GuideContext);
}

interface UseInteractiveGuideParams {
  profileType: ProfileType;
  onNavigate: (page: PageType) => void;
  /** Renvoie la page courante (via ref, pour rester à jour dans les closures async). */
  getCurrentPage: () => PageType;
  t: TFunction;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Clic « natif » : séquence complète d'événements pointeur/souris.
 * Nécessaire pour les composants Radix (onglets Tabs…) qui n'activent PAS
 * sur un simple `el.click()` JS (cf. CLAUDE.md — piège Radix).
 */
function nativeClick(el: HTMLElement) {
  const opts = { bubbles: true, cancelable: true, view: window } as const;
  try { el.dispatchEvent(new PointerEvent("pointerdown", opts)); } catch { /* noop */ }
  el.dispatchEvent(new MouseEvent("mousedown", opts));
  try { el.dispatchEvent(new PointerEvent("pointerup", opts)); } catch { /* noop */ }
  el.dispatchEvent(new MouseEvent("mouseup", opts));
  el.dispatchEvent(new MouseEvent("click", opts));
}

/**
 * Le premier élément VISIBLE qui corresponde au sélecteur.
 *
 * `querySelector` rend le premier du DOM, visible ou non — et l'application en
 * garde volontairement deux exemplaires : la barre de navigation (`hidden lg:flex`)
 * et le menu replié derrière le hamburger. Sous 1024 px, c'est donc la version
 * masquée qui était retenue : driver.js surlignait un élément de taille nulle, et
 * les `reveal` cliquaient dans le vide. Toutes les visites de l'espace encadrant
 * étaient injouables sur mobile et tablette.
 *
 * On se replie sur le premier élément trouvé si AUCUN n'est visible : mieux vaut
 * un popover mal placé qu'une visite qui s'arrête.
 */
function estVisible(el: Element): boolean {
  if ("checkVisibility" in el && typeof el.checkVisibility === "function") {
    if (!el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
  }
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** Attend qu'un élément apparaisse dans le DOM (timeout → null, jamais de rejet). */
function waitForElement(selector: string, timeout = 2500): Promise<Element | null> {
  const chercher = (): Element | null => {
    const tous = Array.from(document.querySelectorAll(selector));
    return tous.find(estVisible) ?? tous[0] ?? null;
  };
  return new Promise((resolve) => {
    const existing = chercher();
    if (existing && estVisible(existing)) return resolve(existing);
    const start = Date.now();
    const tick = () => {
      const el = chercher();
      if (el && estVisible(el)) return resolve(el);
      if (Date.now() - start >= timeout) return resolve(el);
      setTimeout(tick, 80);
    };
    tick();
  });
}

/**
 * Moteur de visite guidée interactive multi-écran (driver.js 1.4.0).
 * Navigue réellement d'un écran à l'autre, exécute les `reveal` (clics d'onglet)
 * et attend le montage de la cible avant de surligner. Cible absente → popover
 * centré (jamais de surbrillance vide).
 */
export function useInteractiveGuide({ profileType, onNavigate, getCurrentPage, t }: UseInteractiveGuideParams) {
  const driverRef = useRef<Driver | null>(null);
  const observerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearObserver = () => {
    if (observerRef.current) { clearInterval(observerRef.current); observerRef.current = null; }
  };

  /** Amène l'app dans l'état requis par l'étape puis attend sa cible. */
  const prepareStep = useCallback(async (step: GuideStep) => {
    if (step.page && getCurrentPage() !== step.page) {
      onNavigate(step.page);
      await delay(150); // laisse React monter la nouvelle page
    }
    if (step.reveal) {
      const rev = await waitForElement(step.reveal);
      if (rev instanceof HTMLElement) {
        nativeClick(rev);
        await delay(150);
      }
    }
    if (step.anchor) {
      await waitForElement(step.anchor);
    }
    await delay(80); // petit temps de stabilisation avant highlight
  }, [getCurrentPage, onNavigate]);

  const runWalkthrough = useCallback(async (id: string) => {
    const w: Walkthrough | undefined = getWalkthrough(id);
    if (!w) return;

    // Ferme une éventuelle visite en cours.
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch { /* noop */ }
      driverRef.current = null;
    }

    const steps = w.steps;
    const driverSteps: DriveStep[] = steps.map((s) => ({
      // Résolution différée : l'élément est requêté au moment du highlight.
      element: s.anchor
        ? ((() => document.querySelector(s.anchor!) || undefined) as unknown as () => Element)
        : undefined,
      // En mode « observation », l'utilisateur pilote l'app (ex. l'assistant) :
      // on masque Suivant/Précédent, la visite avance toute seule (armObserver).
      disableActiveInteraction: false,
      popover: {
        title: t(s.titleKey),
        description: t(s.bodyKey),
        side: s.side,
        align: "start",
        ...(s.observe ? { showButtons: ["close"] as ("close")[] } : {}),
      },
    }));

    // Passe à l'étape suivante (ou termine) — utilisé par l'observateur.
    const advanceFrom = async (i: number) => {
      clearObserver();
      if (i >= steps.length - 1) { d.destroy(); return; }
      await prepareStep(steps[i + 1]);
      d.moveNext();
    };

    // Mode observation : surveille le DOM et avance quand l'utilisateur a agi.
    const armObserver = (i: number) => {
      clearObserver();
      const s = steps[i];
      if (!s || !s.observe) return;
      observerRef.current = setInterval(() => {
        if ((d.getActiveIndex() ?? -1) !== i) { clearObserver(); return; }
        const appeared = s.advanceWhen && document.querySelector(s.advanceWhen);
        const gone = s.advanceWhenGone && !document.querySelector(s.advanceWhenGone);
        if (appeared || gone) { advanceFrom(i); return; }
        // L'utilisateur a fermé la cible (ex. l'assistant) sans aller au bout → on arrête.
        if (s.anchor && !document.querySelector(s.anchor)) { clearObserver(); d.destroy(); }
      }, 200);
    };

    const d = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      nextBtnText: t("guidedTour.next"),
      prevBtnText: t("guidedTour.previous"),
      doneBtnText: t("guidedTour.done"),
      progressText: t("guidedTour.progress"),
      popoverClass: "bizcoach-tour",
      allowClose: true,
      steps: driverSteps,
      onHighlighted: () => armObserver(d.getActiveIndex() ?? 0),
      onDeselected: () => clearObserver(),
      onNextClick: async () => {
        const i = d.getActiveIndex() ?? 0;
        if (i >= steps.length - 1) { d.destroy(); return; }
        await prepareStep(steps[i + 1]);
        d.moveNext();
      },
      onPrevClick: async () => {
        const i = d.getActiveIndex() ?? 0;
        if (i <= 0) return;
        await prepareStep(steps[i - 1]);
        d.movePrevious();
      },
      onPopoverRender: (popover) => {
        const i = d.getActiveIndex() ?? 0;
        // Dernière étape : bouton « En savoir plus » vers le guide écrit.
        if (i === steps.length - 1 && w.guideSectionId) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = t("guide.moreInfo");
          btn.className = "driver-popover-btn bizcoach-tour-more";
          btn.addEventListener("click", () => {
            try { localStorage.setItem(HELP_SCROLL_KEY, w.guideSectionId!); } catch { /* noop */ }
            d.destroy();
            onNavigate("help");
          });
          popover.footerButtons.insertBefore(btn, popover.footerButtons.firstChild);
        }
      },
      onDestroyed: () => { clearObserver(); driverRef.current = null; },
    });

    driverRef.current = d;
    await prepareStep(steps[0]);
    d.drive(0);
  }, [t, onNavigate, prepareStep]);

  return { runWalkthrough, getWalkthroughsForProfile: () => getWalkthroughsForProfile(profileType) };
}
